import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Play, Star, Clock, Loader2 } from 'lucide-react';

/**
 * Standalone embeddable hero slider — meant to be placed in an <iframe> on
 * external sites (e.g. WordPress). NO Navbar / Footer / age-gate.
 *
 * Behaviour:
 *  - Auto-rotates published stories every 6s.
 *  - Clicks open the canonical Intimate.pt URL in a NEW TAB (target="_top" with
 *    `_blank` fallback) so the iframe doesn't trap navigation.
 *  - Honours `?limit=` and `?interval=` query string params.
 */

const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

function getParam(name, fallback) {
  const v = new URLSearchParams(window.location.search).get(name);
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export default function EmbedHero() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const limit = Math.min(10, getParam('limit', 5));
  const interval = Math.max(2000, getParam('interval', 6000));

  useEffect(() => {
    api.get(`/stories?limit=${limit}`)
      .then((res) => {
        const list = (res.data || []).filter((s) => s.cover_image);
        setItems(list);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <Loader2 className="w-6 h-6 animate-spin text-ruby" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black text-gray-400 text-sm">
        Sem contos disponíveis
      </div>
    );
  }

  const current = items[idx];
  const storyUrl = `${SITE_URL}/story/${current.slug || current.id}`;

  return (
    <div className="w-full h-screen relative overflow-hidden bg-black font-sans" data-testid="embed-hero">
      {/* Background slides */}
      {items.map((it, i) => (
        <div
          key={it.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={it.cover_image}
            alt=""
            className="w-full h-full object-cover"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Gradients */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-end md:items-center">
        <div className="px-5 md:px-10 pb-6 md:pb-0 max-w-2xl">
          <div className="inline-block px-3 py-1 rounded-full bg-ruby/90 text-white text-xs font-semibold mb-3 tracking-wide">
            {current.is_premium ? 'Premium' : 'Conto em Destaque'}
          </div>

          <h1
            className="text-2xl md:text-4xl font-bold text-white mb-3 leading-tight line-clamp-2"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            {current.title}
          </h1>

          {(current.synopsis || current.excerpt) && (
            <p className="text-gray-200 text-sm md:text-base mb-4 line-clamp-2 max-w-xl">
              {current.synopsis || current.excerpt}
            </p>
          )}

          <div className="flex items-center gap-3 text-xs md:text-sm text-gray-300 mb-5">
            {current.reading_time > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {current.reading_time} min
              </span>
            )}
            {current.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                {current.rating.toFixed(1)}
              </span>
            )}
            {current.author_name && <span className="opacity-80">{current.author_name}</span>}
          </div>

          <a
            href={storyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="embed-hero-cta"
            className="inline-flex items-center gap-2 bg-ruby hover:bg-ruby/90 text-white rounded-full px-5 md:px-7 py-2.5 md:py-3 font-semibold transition-colors"
          >
            <Play className="w-4 h-4 fill-white" />
            Ler na Intimate.pt
          </a>
        </div>
      </div>

      {/* Pagination dots */}
      {items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Ir para slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-7 bg-ruby' : 'w-2.5 bg-white/30 hover:bg-white/60'}`}
            />
          ))}
        </div>
      )}

      {/* Discreet brand tag */}
      <a
        href={SITE_URL || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-3 right-4 z-20 text-[11px] uppercase tracking-widest text-white/60 hover:text-white"
        style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
      >
        Intimate.pt
      </a>
    </div>
  );
}
